import { Box } from '@chakra-ui/react'
import { useEffect, useRef } from 'react'
import maplibregl, { Popup, StyleSpecification } from 'maplibre-gl'

// @ts-ignore
import 'maplibre-gl/dist/maplibre-gl.css'

import { cellToBoundary, cellToLatLng } from 'h3-js'

import { getMapQueryShape } from './get-query-shape'
import moment from 'moment'

export type ViewMapChange = {
    shape: QueryShape
    zoom: number
    center: {
        lng: number
        lat: number
    }
}

export type ViewMapProps = {
    initialCenter?: {
        lng: number
        lat: number
    }
    initialZoom?: number
    onChange?: (change: ViewMapChange) => void
    data:
        | {
              casts: AggregatedCast[]
              type: 'aggregated'
          }
        | {
              casts: RawCast[]
              type: 'raw'
          }
        | undefined
}

export type Polygon = [number, number][]

export type QueryShape =
    | {
          type: 'bbox'
          minLng: number
          maxLng: number
          minLat: number
          maxLat: number
      }
    | {
          type: 'wide'
          minLat: number
          maxLat: number
      }
    | {
          type: 'world'
          minLat: number
          maxLat: number
      }

export class AggregatedCast {
    constructor(
        readonly h3: string,
        readonly installations: number,
        readonly statics: number,
        readonly trollings: number,
        readonly manuals: number
    ) {}
}

export type Position = {
    long: number
    lat: number
    distance: number
}

export class RawCast {
    constructor(
        readonly long: number,
        readonly lat: number,
        readonly timestamp: Date,
        readonly meta: Partial<{
            depth: {
                max: number
                avg: number
            }
            duration: number
            distance: number
            result: string
        }>,
        readonly positions: Position[] | undefined
    ) {}
}

const RAW_SOURCE_ID = 'casts-raw-source'
const RAW_LAYER_ID = 'casts-raw-layer'
const RAW_TEXT_LAYER_ID = 'casts-raw-text-layer'

const RAW_LINE_SOURCE_ID = 'casts-raw-line-source'
const RAW_LINE_LAYER_ID = 'casts-raw-line-layer'

const RAW_START_LABEL_SOURCE_ID = 'casts-raw-start-label-source'
const RAW_START_LABEL_LAYER_ID = 'casts-raw-start-label-layer'

const RAW_END_MARKER_SOURCE_ID = 'casts-raw-end-marker-source'
const RAW_END_MARKER_LAYER_ID = 'casts-raw-end-marker-layer'

const RAW_TRACK_MARKER_COLOR = '#FFD700'

const TRIANGLE_ICON_ID = 'casts-raw-start-triangle-icon'
const TRIANGLE_ICON_LOGICAL_SIZE = 16.5
const TRIANGLE_ICON_PIXEL_RATIO = 2

const AGG_POLYGON_SOURCE_ID = 'casts-agg-polygons-source'
const AGG_POLYGON_LAYER_ID = 'casts-agg-polygons-layer'
const AGG_POLYGON_OUTLINE_LAYER_ID = 'casts-agg-polygons-outline-layer'

const AGG_CENTER_SOURCE_ID = 'casts-agg-centers-source'
const AGG_TITLE_LAYER_ID = 'casts-agg-title-layer'
const AGG_SUBTITLE_LAYER_ID = 'casts-agg-subtitle-layer'

const AGG_TITLE_STYLE = {
    fontSize: 14,
    font: ['Open Sans Bold', 'Arial Unicode MS Bold'],
    color: '#ffffff',
    haloColor: '#000000',
    haloWidth: 1.2,

    // Negative moves the title upward.
    offset: -0.55
}

const AGG_SUBTITLE_STYLE = {
    fontSize: 10,
    font: ['Open Sans Semibold', 'Arial Unicode MS Bold'],
    color: '#ffffff',
    haloColor: '#000000',
    haloWidth: 1,

    // Positive moves the subtitle downward.
    offset: 0.75
}

const OSM_STYLE: StyleSpecification = {
    version: 8,
    sources: {
        osm: {
            type: 'raster',
            tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
            tileSize: 256,
            attribution: '&copy; OpenStreetMap contributors'
        }
    },
    layers: [{ id: 'osm', type: 'raster', source: 'osm' }]
}

// Draws a filled triangle pointing "up". icon-rotate then turns it to face
// the bearing of the cast's first segment.
function createTriangleIcon(): ImageData {
    const size = TRIANGLE_ICON_LOGICAL_SIZE * TRIANGLE_ICON_PIXEL_RATIO
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size

    const ctx = canvas.getContext('2d')
    if (!ctx) {
        return new ImageData(size, size)
    }

    // Small margin so anti-aliasing doesn't clip against the canvas edge.
    const margin = TRIANGLE_ICON_PIXEL_RATIO

    ctx.beginPath()
    ctx.moveTo(size / 2, margin)
    ctx.lineTo(size - margin, size - margin)
    ctx.lineTo(margin, size - margin)
    ctx.closePath()

    ctx.fillStyle = RAW_TRACK_MARKER_COLOR
    ctx.fill()

    return ctx.getImageData(0, 0, size, size)
}

function ensureTriangleIcon(map: maplibregl.Map): void {
    if (!map.hasImage(TRIANGLE_ICON_ID)) {
        map.addImage(TRIANGLE_ICON_ID, createTriangleIcon(), {
            pixelRatio: TRIANGLE_ICON_PIXEL_RATIO
        })
    }
}

// Initial compass bearing (degrees clockwise from true north) from `from` to
// `to`, matching maplibre's icon-rotate convention under 'map' alignment.
function computeBearingDegrees(
    from: { long: number; lat: number },
    to: { long: number; lat: number }
): number {
    const toRad = (deg: number) => (deg * Math.PI) / 180
    const toDeg = (rad: number) => (rad * 180) / Math.PI

    const lat1 = toRad(from.lat)
    const lat2 = toRad(to.lat)
    const dLon = toRad(to.long - from.long)

    const y = Math.sin(dLon) * Math.cos(lat2)
    const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon)

    return (toDeg(Math.atan2(y, x)) + 360) % 360
}

function removeLayerIfExists(map: maplibregl.Map, layerId: string): void {
    if (map.getLayer(layerId)) {
        map.removeLayer(layerId)
    }
}

function removeSourceIfExists(map: maplibregl.Map, sourceId: string): void {
    if (map.getSource(sourceId)) {
        map.removeSource(sourceId)
    }
}

function clearCastLayers(map: maplibregl.Map): void {
    removeLayerIfExists(map, RAW_TEXT_LAYER_ID)
    removeLayerIfExists(map, RAW_LAYER_ID)
    removeLayerIfExists(map, RAW_START_LABEL_LAYER_ID)
    removeLayerIfExists(map, RAW_END_MARKER_LAYER_ID)
    removeLayerIfExists(map, RAW_LINE_LAYER_ID)

    removeLayerIfExists(map, AGG_SUBTITLE_LAYER_ID)
    removeLayerIfExists(map, AGG_TITLE_LAYER_ID)
    removeLayerIfExists(map, AGG_POLYGON_OUTLINE_LAYER_ID)
    removeLayerIfExists(map, AGG_POLYGON_LAYER_ID)

    removeSourceIfExists(map, RAW_SOURCE_ID)
    removeSourceIfExists(map, RAW_START_LABEL_SOURCE_ID)
    removeSourceIfExists(map, RAW_END_MARKER_SOURCE_ID)
    removeSourceIfExists(map, RAW_LINE_SOURCE_ID)
    removeSourceIfExists(map, AGG_CENTER_SOURCE_ID)
    removeSourceIfExists(map, AGG_POLYGON_SOURCE_ID)
}

function toAggregatedPolygonGeoJson(casts: AggregatedCast[]): GeoJSON.FeatureCollection {
    return {
        type: 'FeatureCollection',
        features: casts.map((cast) => {
            const boundary = cellToBoundary(cast.h3).map(([lat, long]): [number, number] => [
                long,
                lat
            ])

            const closedBoundary = [...boundary, boundary[0]]

            return {
                type: 'Feature',
                properties: {
                    h3: cast.h3,
                    trollings: cast.trollings,
                    statics: cast.statics,
                    manuals: cast.manuals,
                    installations: cast.installations,
                    total: cast.trollings + cast.statics + cast.manuals
                },
                geometry: {
                    type: 'Polygon',
                    coordinates: [closedBoundary]
                }
            } satisfies GeoJSON.Feature
        })
    }
}

function toAggregatedCenterGeoJson(casts: AggregatedCast[]): GeoJSON.FeatureCollection {
    return {
        type: 'FeatureCollection',
        features: casts.map((cast) => {
            const [lat, long] = cellToLatLng(cast.h3)
            const total = cast.trollings + cast.statics + cast.manuals

            return {
                type: 'Feature',
                properties: {
                    h3: cast.h3,
                    trollings: cast.trollings,
                    statics: cast.statics,
                    manuals: cast.manuals,
                    installations: cast.installations,
                    total,

                    title: `${cast.installations}`,
                    subtitle: ['statics', 'trollings', 'manuals']
                        .flatMap((type) => {
                            const count = cast[type as keyof AggregatedCast] as number

                            if ((count ?? 0) > 0) {
                                return [`${count}${type[0]}`]
                            }

                            return []
                        })
                        .join('/')
                },
                geometry: {
                    type: 'Point',
                    coordinates: [long, lat]
                }
            } satisfies GeoJSON.Feature
        })
    }
}

function flattenMeta(
    input: unknown,
    prefix = ''
): Record<string, string | number | boolean | null> {
    if (!input || typeof input !== 'object') {
        return {}
    }

    const out: Record<string, string | number | boolean | null> = {}

    for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
        const path = prefix ? `${prefix}.${key}` : key

        if (
            (value == null ||
                typeof value === 'string' ||
                typeof value === 'number' ||
                typeof value === 'boolean') &&
            !key.includes('$')
        ) {
            out[path] = value as string | number | boolean | null
            continue
        }

        if (Array.isArray(value)) {
            out[path] = value.join(', ')
            continue
        }

        if (typeof value === 'object') {
            Object.assign(out, flattenMeta(value, path))
        }
    }

    return out
}

function translateMetaKey(path: string): string {
    const translations: Record<string, string> = {
        'depth.max': 'Max depth',
        'depth.avg': 'Avg depth',
        duration: 'Duration',
        distance: 'Distance',
        result: 'Result'
    }

    return translations[path] ?? path
}

function formatMetaValue(path: string, value: unknown): string {
    if (value == null) {
        return '-'
    }

    if (typeof value !== 'number') {
        return String(value)
    }

    const formatted = Number.isInteger(value)
        ? String(value)
        : String(Math.round(value * 100) / 100)

    switch (path) {
        case 'depth.max':
        case 'depth.avg':
        case 'distance':
            return `${formatted} m`

        case 'duration':
            return `${formatted} s`

        default:
            return formatted
    }
}

function escapeHtml(input: string): string {
    return input
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;')
}

function buildRawPopupHtml(
    timestampText: string,
    metaFlat: Record<string, string | number | boolean | null>
): string {
    const lines = [
        `<div><strong>Timestamp:</strong> ${escapeHtml(timestampText)}</div>`,

        ...Object.entries(metaFlat).map(
            ([key, value]) =>
                `<div><strong>${escapeHtml(
                    translateMetaKey(key)
                )}:</strong> ${escapeHtml(formatMetaValue(key, value))}</div>`
        )
    ]

    return `
        <div style="
            padding: 10px 12px;
            border: 1px solid #CBD5E0;
            border-radius: 10px;
            background: white;
            color: #1A202C;
            box-shadow: 0 6px 20px rgba(0,0,0,0.12);
            font-size: 12px;
            line-height: 1.45;
            min-width: 170px;
        ">
            ${lines.join('')}
        </div>
    `
}

function createRawCastLabel(cast: RawCast): string {
    const parts: string[] = []
    if (cast.meta) {
        const meta = cast.meta
        if (meta.depth) {
            parts.push(`${meta.depth.max.toFixed(1)}m`)
        }

        if (meta.distance) {
            parts.push(`${(meta.distance / 1000).toFixed(1)}km`)
        }

        if (meta.duration) {
            parts.push(`${Math.round(meta.duration)}s`)
        }
    }

    return parts.join(' / ')
}

function hasTrackPositions(cast: RawCast): cast is RawCast & { positions: Position[] } {
    return Boolean(cast.positions && cast.positions.length >= 2)
}

function formatDistanceKm(distanceMeters: number | undefined): string {
    if (distanceMeters == null) {
        return '-'
    }

    return `${(distanceMeters / 1000).toFixed(1)}km`
}

function formatDepthM(depthMeters: number | undefined): string {
    if (depthMeters == null) {
        return '-'
    }

    return `${depthMeters.toFixed(1)}m`
}

function formatDurationClock(durationSeconds: number | undefined): string {
    if (durationSeconds == null) {
        return '-'
    }

    const totalSeconds = Math.round(durationSeconds)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    const pad = (n: number) => String(n).padStart(2, '0')

    if (hours > 0) {
        return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    }

    if (minutes > 0) {
        return `${pad(minutes)}:${pad(seconds)}`
    }

    return `${seconds}`
}

function createRawCastStartLabel(cast: RawCast & { positions: Position[] }): string {
    const lastPosition = cast.positions[cast.positions.length - 1]
    const distance = formatDistanceKm(lastPosition.distance)
    const depth = formatDepthM(cast.meta?.depth?.max)
    const duration = formatDurationClock(cast.meta?.duration)

    return `${distance} at ${depth} (${duration})`
}

function toRawLineGeoJson(casts: RawCast[]): GeoJSON.FeatureCollection {
    return {
        type: 'FeatureCollection',
        features: casts.filter(hasTrackPositions).map((cast) => {
            return {
                type: 'Feature',
                properties: {},
                geometry: {
                    type: 'LineString',
                    coordinates: cast.positions.map((p): [number, number] => [p.long, p.lat])
                }
            } satisfies GeoJSON.Feature
        })
    }
}

function toRawGeoJson(casts: RawCast[]): GeoJSON.FeatureCollection {
    return {
        type: 'FeatureCollection',
        features: casts
            .filter((cast) => !hasTrackPositions(cast))
            .map((cast) => {
                const metaFlat = flattenMeta(cast.meta)
                const timestampText = moment(cast.timestamp).format('YYYY-MM-DD HH:mm')

                return {
                    type: 'Feature',
                    properties: {
                        timestamp: timestampText,
                        castLabel: createRawCastLabel(cast),
                        popupHtml: buildRawPopupHtml(timestampText, metaFlat),

                        ...Object.fromEntries(
                            Object.entries(metaFlat).map(([key, value]) => [
                                `meta:${key}`,
                                value ?? ''
                            ])
                        )
                    },
                    geometry: {
                        type: 'Point',
                        coordinates: [cast.long, cast.lat]
                    }
                } satisfies GeoJSON.Feature
            })
    }
}

function toRawStartLabelGeoJson(casts: RawCast[]): GeoJSON.FeatureCollection {
    return {
        type: 'FeatureCollection',
        features: casts.filter(hasTrackPositions).map((cast) => {
            const metaFlat = flattenMeta(cast.meta)
            const timestampText = moment(cast.timestamp).format('YYYY-MM-DD HH:mm')
            const start = cast.positions[0]

            return {
                type: 'Feature',
                properties: {
                    timestamp: timestampText,
                    startLabel: createRawCastStartLabel(cast),
                    bearing: computeBearingDegrees(start, cast.positions[1]),
                    popupHtml: buildRawPopupHtml(timestampText, metaFlat),

                    ...Object.fromEntries(
                        Object.entries(metaFlat).map(([key, value]) => [`meta:${key}`, value ?? ''])
                    )
                },
                geometry: {
                    type: 'Point',
                    coordinates: [start.long, start.lat]
                }
            } satisfies GeoJSON.Feature
        })
    }
}

function toRawEndMarkerGeoJson(casts: RawCast[]): GeoJSON.FeatureCollection {
    return {
        type: 'FeatureCollection',
        features: casts.filter(hasTrackPositions).map((cast) => {
            const metaFlat = flattenMeta(cast.meta)
            const timestampText = moment(cast.timestamp).format('YYYY-MM-DD HH:mm')
            const end = cast.positions[cast.positions.length - 1]

            return {
                type: 'Feature',
                properties: {
                    timestamp: timestampText,
                    popupHtml: buildRawPopupHtml(timestampText, metaFlat),

                    ...Object.fromEntries(
                        Object.entries(metaFlat).map(([key, value]) => [`meta:${key}`, value ?? ''])
                    )
                },
                geometry: {
                    type: 'Point',
                    coordinates: [end.long, end.lat]
                }
            } satisfies GeoJSON.Feature
        })
    }
}

function renderData(
    map: maplibregl.Map,
    data:
        | {
              casts: AggregatedCast[]
              type: 'aggregated'
          }
        | {
              casts: RawCast[]
              type: 'raw'
          }
        | undefined
): void {
    if (!map.isStyleLoaded()) {
        return
    }

    clearCastLayers(map)

    if (!data) {
        return
    }

    if (data.type === 'raw') {
        // Non-track casts' circles are added first so we can explicitly
        // stack the track line beneath them (via beforeId) regardless of
        // insertion order elsewhere.
        map.addSource(RAW_SOURCE_ID, {
            type: 'geojson',
            data: toRawGeoJson(data.casts)
        })

        map.addLayer({
            id: RAW_LAYER_ID,
            type: 'circle',
            source: RAW_SOURCE_ID,
            paint: {
                'circle-radius': 18,
                'circle-color': '#EDF2F7',
                'circle-opacity': 0.96,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#1A202C'
            }
        })

        map.addLayer({
            id: RAW_TEXT_LAYER_ID,
            type: 'symbol',
            source: RAW_SOURCE_ID,
            layout: {
                'text-field': ['get', 'castLabel'],
                'text-size': 10,
                'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
                'text-anchor': 'center',
                'text-allow-overlap': true,
                'text-ignore-placement': true
            },
            paint: {
                'text-color': '#1A202C'
            }
        })

        map.addSource(RAW_LINE_SOURCE_ID, {
            type: 'geojson',
            data: toRawLineGeoJson(data.casts)
        })

        map.addLayer(
            {
                id: RAW_LINE_LAYER_ID,
                type: 'line',
                source: RAW_LINE_SOURCE_ID,
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': '#FFD700',
                    'line-width': 3,
                    'line-opacity': 0.9
                }
            },
            RAW_LAYER_ID
        )

        map.addSource(RAW_END_MARKER_SOURCE_ID, {
            type: 'geojson',
            data: toRawEndMarkerGeoJson(data.casts)
        })

        map.addLayer({
            id: RAW_END_MARKER_LAYER_ID,
            type: 'circle',
            source: RAW_END_MARKER_SOURCE_ID,
            paint: {
                'circle-radius': 6,
                'circle-color': RAW_TRACK_MARKER_COLOR,
                'circle-opacity': 1
            }
        })

        ensureTriangleIcon(map)

        map.addSource(RAW_START_LABEL_SOURCE_ID, {
            type: 'geojson',
            data: toRawStartLabelGeoJson(data.casts)
        })

        map.addLayer({
            id: RAW_START_LABEL_LAYER_ID,
            type: 'symbol',
            source: RAW_START_LABEL_SOURCE_ID,
            layout: {
                'icon-image': TRIANGLE_ICON_ID,
                'icon-anchor': 'center',
                'icon-rotate': ['get', 'bearing'],
                'icon-rotation-alignment': 'map',
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,

                'text-field': ['get', 'startLabel'],
                'text-size': 11,
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-anchor': 'left',
                'text-offset': [1.4, 0],
                'text-allow-overlap': true,
                'text-ignore-placement': true
            },
            paint: {
                'text-color': '#1A202C',
                'text-halo-color': '#FFFFFF',
                'text-halo-width': 1.5
            }
        })

        return
    }

    map.addSource(AGG_POLYGON_SOURCE_ID, {
        type: 'geojson',
        data: toAggregatedPolygonGeoJson(data.casts)
    })

    map.addLayer({
        id: AGG_POLYGON_LAYER_ID,
        type: 'fill',
        source: AGG_POLYGON_SOURCE_ID,
        paint: {
            'fill-color': '#4A5568',
            'fill-opacity': 0.35
        }
    })

    map.addLayer({
        id: AGG_POLYGON_OUTLINE_LAYER_ID,
        type: 'line',
        source: AGG_POLYGON_SOURCE_ID,
        paint: {
            'line-color': '#2D3748',
            'line-width': ['interpolate', ['linear'], ['zoom'], 0, 0.75, 3, 1, 8, 1.5],
            'line-opacity': 0.9
        }
    })

    map.addSource(AGG_CENTER_SOURCE_ID, {
        type: 'geojson',
        data: toAggregatedCenterGeoJson(data.casts)
    })

    map.addLayer({
        id: AGG_TITLE_LAYER_ID,
        type: 'symbol',
        source: AGG_CENTER_SOURCE_ID,
        layout: {
            'text-field': ['get', 'title'],
            'text-size': AGG_TITLE_STYLE.fontSize,
            'text-font': AGG_TITLE_STYLE.font,
            'text-anchor': 'center',
            'text-offset': [0, AGG_TITLE_STYLE.offset],
            'text-allow-overlap': true,
            'text-ignore-placement': true
        },
        paint: {
            'text-color': AGG_TITLE_STYLE.color,
            'text-halo-color': AGG_TITLE_STYLE.haloColor,
            'text-halo-width': AGG_TITLE_STYLE.haloWidth
        }
    })

    map.addLayer({
        id: AGG_SUBTITLE_LAYER_ID,
        type: 'symbol',
        source: AGG_CENTER_SOURCE_ID,
        layout: {
            'text-field': ['get', 'subtitle'],
            'text-size': AGG_SUBTITLE_STYLE.fontSize,
            'text-font': AGG_SUBTITLE_STYLE.font,
            'text-anchor': 'center',
            'text-offset': [0, AGG_SUBTITLE_STYLE.offset],
            'text-allow-overlap': true,
            'text-ignore-placement': true
        },
        paint: {
            'text-color': AGG_SUBTITLE_STYLE.color,
            'text-halo-color': AGG_SUBTITLE_STYLE.haloColor,
            'text-halo-width': AGG_SUBTITLE_STYLE.haloWidth
        }
    })
}

export function ViewMap({
    initialCenter = { lng: 0, lat: 20 },
    initialZoom = 1,
    onChange,
    data
}: ViewMapProps) {
    const containerRef = useRef<HTMLDivElement | null>(null)
    const mapRef = useRef<maplibregl.Map | null>(null)
    const popupRef = useRef<Popup | null>(null)
    const onChangeRef = useRef<typeof onChange>(onChange)
    const dataRef = useRef<typeof data>(data)

    useEffect(() => {
        onChangeRef.current = onChange
    }, [onChange])

    useEffect(() => {
        dataRef.current = data

        if (mapRef.current) {
            renderData(mapRef.current, data)
        }
    }, [data])

    useEffect(() => {
        if (!containerRef.current || mapRef.current) {
            return
        }

        const map = new maplibregl.Map({
            container: containerRef.current,
            style: OSM_STYLE,
            zoom: initialZoom,
            center: [initialCenter.lng, initialCenter.lat],
            renderWorldCopies: true
        })

        mapRef.current = map

        popupRef.current = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 14,
            className: 'raw-cast-popup'
        })

        const emitChange = () => {
            const callback = onChangeRef.current

            if (!callback) {
                return
            }

            const center = map.getCenter()

            callback({
                shape: getMapQueryShape(map),
                zoom: map.getZoom(),
                center: {
                    lng: center.lng,
                    lat: center.lat
                }
            })
        }

        const showRawPopup = (
            event: maplibregl.MapMouseEvent & {
                features?: maplibregl.MapGeoJSONFeature[]
            }
        ) => {
            const feature = event.features?.[0]

            if (!feature || feature.geometry.type !== 'Point') {
                return
            }

            const html =
                typeof feature.properties?.popupHtml === 'string'
                    ? feature.properties.popupHtml
                    : ''

            const coordinates = [...feature.geometry.coordinates] as [number, number]

            while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360
            }

            map.getCanvas().style.cursor = 'pointer'

            popupRef.current?.setLngLat(coordinates).setHTML(html).addTo(map)
        }

        const hideRawPopup = () => {
            map.getCanvas().style.cursor = ''
            popupRef.current?.remove()
        }

        map.on('load', () => {
            renderData(map, dataRef.current)
            emitChange()

            map.on('mouseenter', RAW_LAYER_ID, showRawPopup)
            map.on('mousemove', RAW_LAYER_ID, showRawPopup)
            map.on('mouseleave', RAW_LAYER_ID, hideRawPopup)

            map.on('mouseenter', RAW_TEXT_LAYER_ID, showRawPopup)
            map.on('mousemove', RAW_TEXT_LAYER_ID, showRawPopup)
            map.on('mouseleave', RAW_TEXT_LAYER_ID, hideRawPopup)

            map.on('mouseenter', RAW_START_LABEL_LAYER_ID, showRawPopup)
            map.on('mousemove', RAW_START_LABEL_LAYER_ID, showRawPopup)
            map.on('mouseleave', RAW_START_LABEL_LAYER_ID, hideRawPopup)

            map.on('mouseenter', RAW_END_MARKER_LAYER_ID, showRawPopup)
            map.on('mousemove', RAW_END_MARKER_LAYER_ID, showRawPopup)
            map.on('mouseleave', RAW_END_MARKER_LAYER_ID, hideRawPopup)
        })

        map.on('moveend', emitChange)
        map.on('zoomend', emitChange)

        return () => {
            popupRef.current?.remove()
            map.remove()
            mapRef.current = null
        }
    }, [])

    return <Box ref={containerRef} w="100%" h="100%" borderRadius="md" overflow="hidden" />
}
